import Guid from "~/helpers/guid";
import { RelationType } from "~/models/relationType";
import IRepo from "../../../common/interfaces/repo";
import Validatable from "../../../common/mixins/validatable";
import CertificateBuilderModel from "../../../common/models/tracked/certificateBuilderModel";
import Repo from "../../../common/state/modules/repo";
import { CertificateDynamicInputIcons, CertificateDynamicInputType, SampleValueCertificateDynamicInputTypes } from "../enums/certificateDynamicInputType";
import Certificate from "./certificate";
import CertificateDynamicInputContainer from "./certificateDynamicInputContainer";
import CertificateDynamicInputFactory from "./certificateDynamicInputFactory";
import {ModelType} from "~/enums/modelType";
import {GenericType} from "~/interfaces/genericTypeInterfaces";
import {Logger} from "~/services/logger";
import {IImposter, IImposterFactory, IImposterMap, IImposterOptions} from "~/interfaces/imposterInterfaces";
import {ObjectHelper} from "~/helpers/objectHelper";
import Imposter from "~/models/imposter";
import {commonImposterMap} from "~/models/imposterMapFactories/commonImposterMap";
import {ReferenceNodeType} from "~/enums/referenceNodeType";

export default class CertificateDynamicInput extends CertificateDynamicInputContainer(Validatable(CertificateBuilderModel)) implements IImposterFactory {

  static repo: IRepo<CertificateDynamicInput> = new Repo<CertificateDynamicInput>('certificateDynamicInputs', CertificateDynamicInput);

  client_id: string;
  name: string = "";
  type: number = CertificateDynamicInputType.Text;
  sort_order: number = 0;
  is_list: boolean = false;
  is_optional: boolean = false;
  static_value: string = ""
  sample_value: string = ""

  get rootName(): string {
    return 'certificate_dynamic_inputs';
  }

  createEmbeddedObject = {
    certificate_dynamic_inputs: (parent, json): CertificateDynamicInput => {
      return CertificateDynamicInputFactory.create(this, json, false);
    },
  };

  get EMBEDDED_MODELS(): { [p: string]: { type: any; relationType: RelationType } } {
    return {
      'certificate_dynamic_inputs': {type: CertificateDynamicInput, relationType: RelationType.dictionary},
    };
  }

  definePropertiesFrom(parent: Certificate | CertificateDynamicInput, json: object) {
    this.defineProperty('client_id', json["client_id"] || Guid.create());
    this.defineProperty("name", json['name'] || this.name);
    this.defineProperty<this>("type", json['type'] || this.type, false, null, (certificateDynamicInput, oldType, newType) => {
      if (!SampleValueCertificateDynamicInputTypes.includes(newType)) {
        certificateDynamicInput.sample_value = null
      }
    });
    this.defineProperty('sort_order', json['sort_order'] || this.sort_order);
    this.defineProperty('is_list', json['is_list'] || this.is_list);
    this.defineProperty('is_optional', json['is_optional'] || this.is_optional);
    this.defineProperty('static_value', json['static_value'] || this.static_value);
    this.defineProperty('sample_value', json['sample_value'] || this.sample_value)
  }

  afterDestroy() {
    super.afterDestroy();
    this.parent.recalculateDynamicInputPositions();
  }

  get typeDisplay(): string {
    const name = CertificateDynamicInputType[this.type];
    if (this.is_list) {
      return name + ' List';
    } else {
      return name;
    }
  }

  get typeIcon(): string {
    return CertificateDynamicInputIcons[this.type];
  }

  getDisplayName(): string {
    return "Dynamic Inputs"
  }

  get referenceNameOrDefault(): string {
    return this.name;
  }

  get isList(): boolean {
    return this.is_list || this.type === CertificateDynamicInputType.Repeatable;
  }

  /**
   * if the data of interest is itself, a collection of data
   */
  get isDataCollection(): boolean {
    return this.isList
  }

  get modelType(): ModelType {
    return ModelType.CertificateDynamicInput;
  }

  get genericType(): GenericType {
    switch (this.type) {
      case CertificateDynamicInputType.Text:
        return ModelType.Text
      case CertificateDynamicInputType.Number:
        return ModelType.Number
      case CertificateDynamicInputType.Object:
        return ModelType.Object
      case CertificateDynamicInputType.File: // fallthrough
      case CertificateDynamicInputType.Image:
        return ModelType.File
      case CertificateDynamicInputType.Signature:
        return ModelType.Signature;
      default:
        Logger.errorOnce('CertificateDynamicInput', `CertificateDynamicInputType ${this.type} not implemented yet`)
    }
  }

  get referenceNodeType(): ReferenceNodeType {
    return ReferenceNodeType.CertificateDynamicInput;
  }

  imposterFor({ modelType, options }: { modelType: ModelType, options?: IImposterOptions }): IImposter {
    let map = this.imposterMapFor({ modelType, options });
    if (!map) {
      map = {
        id: this.id,
        ...ObjectHelper.sliceProperties(this as CertificateDynamicInput, 'genericType', 'modelType'),
        isCollection: undefined, // set below
      };
    }

    if (options.hasOwnProperty('isCollection')) {
      map.isCollection = options.isCollection;
    } else {
      map.isCollection = this.isList;
    }

    return new Imposter(map, options);
  }

  imposterMapFor({ modelType, options }: { modelType: ModelType, options?: IImposterOptions }): IImposterMap {
    let source = {modelType: this.modelType, genericType: this.genericType};

    let map: IImposterMap;
    if (this.type === CertificateDynamicInputType.Signature) {
      map = {
        id: null,
        genericType: ModelType.Signature,
        modelType: ModelType.CertificateDynamicInput,
        isCollection: null,
      }
    }

    return map || commonImposterMap({
        destination: {modelType, genericType: options.genericType},
        source,
        options,
      },
    );
  }

}